import type { MinuteRange } from '@reditelji/shared';
import type { PoolVolunteer, TimelinePayload } from '../api/types';
import { useAxis } from './axisContext';
import { availabilityBands, availabilitySummary, personDayShifts, range } from './derive';
import { hourTicks, minToPct, spanPct } from './geometry';

/**
 * A person's day on the board's own axis: what their signup sheet said per
 * slot, the rows they already hold, and — for a candidate — the piece they are
 * weighed for, red when it overrules something or clashes. Hours the sheet
 * never asked about stay blank.
 */
export function DayStrip({
  volunteer,
  data,
  piece,
  bad,
  size = 'card',
}: {
  volunteer: PoolVolunteer;
  data: TimelinePayload;
  piece?: MinuteRange;
  bad?: boolean;
  size?: 'card' | 'lens';
}) {
  const axis = useAxis();
  const bands = availabilityBands(volunteer.availability, data.day.key);
  const shifts = personDayShifts(data, volunteer.id);
  const place = (r: MinuteRange) => ({
    left: `${minToPct(axis, r.fromMin)}%`,
    width: `${spanPct(axis, r.fromMin, r.toMin)}%`,
  });
  const summary = [
    availabilitySummary(volunteer.availability, data.day.key),
    shifts.length > 0 ? `izmene: ${shifts.map((s) => `${s.postCode} ${range(s.fromMin, s.toMin)}`).join(', ')}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className={`tl-ds tl-ds--${size}`} role="img" aria-label={summary} title={summary}>
      {size === 'lens'
        ? hourTicks(axis)
            .filter((m) => m % 180 === 0)
            .map((m) => <i key={`t${m}`} className="tl-ds-tick" style={{ left: `${minToPct(axis, m)}%` }} />)
        : null}
      {bands.map((b) => (
        <i key={b.fromMin} className={`tl-ds-band tl-ds-band--${b.value}`} style={place(b)} />
      ))}
      {shifts.map((s) => (
        <i key={s.id} className="tl-ds-shift" style={place(s)} />
      ))}
      {piece ? <i className={`tl-ds-piece${bad ? ' tl-ds-piece--bad' : ''}`} style={place(piece)} /> : null}
    </div>
  );
}

/** What the strip's colours mean, said once above a list of them. */
export function DayStripLegend() {
  return (
    <span className="tl-ds-legend">
      <span><i className="tl-ds-swatch tl-ds-band--na-voljo" />na voljo</span>
      <span><i className="tl-ds-swatch tl-ds-band--delno" />delno</span>
      <span><i className="tl-ds-swatch tl-ds-band--ni-na-voljo" />ni na voljo</span>
      <span><i className="tl-ds-swatch tl-ds-band--neznano" />ni podatka</span>
      <span><i className="tl-ds-swatch tl-ds-shift" />izmena</span>
    </span>
  );
}
